import React, { useEffect, useState, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Plus, Trash2, Circle, Clock, Pause, Play, ArrowRightLeft, Pencil, Check, X as XIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ROLE_LABELS = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  operator: 'Operador',
};

function formatLastSeen(lastSeen) {
  if (!lastSeen) return 'Nunca acessou';
  try {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Agora mesmo';
    if (diffMin < 60) return `${diffMin} min atras`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h atras`;
    return date.toLocaleString('pt-BR');
  } catch {
    return 'Desconhecido';
  }
}

export default function OperatorsPage() {
  const { user } = useAuth();
  const [operators, setOperators] = useState([]);
  const [onlineStatus, setOnlineStatus] = useState({ users: [], online_count: 0, total_count: 0 });
  const [teams, setTeams] = useState([]);
  const [editingTeamName, setEditingTeamName] = useState(null);
  const [teamNameInput, setTeamNameInput] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });

  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isPrivileged = isAdmin || isSupervisor;
  const canSeeStatus = isPrivileged;
  const TEAM_MANAGERS = ['admin@biotop.com'];
  const canManageTeams = isPrivileged;

  const fetchOperators = useCallback(async () => {
    try {
      const response = await api.getOperators();
      setOperators(response.data);
    } catch {
      toast.error('Erro ao carregar operadores');
    }
  }, []);

  const fetchOnlineStatus = useCallback(async () => {
    if (!canSeeStatus) return;
    try {
      const response = await api.getOnlineStatus();
      setOnlineStatus(response.data);
    } catch {}
  }, [canSeeStatus]);

  const fetchTeams = useCallback(async () => {
    if (!isPrivileged) return;
    try {
      const response = await api.getTeams();
      setTeams(response.data);
    } catch {}
  }, [isPrivileged]);

  useEffect(() => {
    fetchOperators();
    fetchOnlineStatus();
    fetchTeams();
    const interval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchOperators, fetchOnlineStatus, fetchTeams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createOperator(formData);
      toast.success('Operador criado com sucesso!');
      setShowCreateDialog(false);
      setFormData({ name: '', email: '', password: '', phone: '' });
      fetchOperators();
    } catch {
      toast.error('Erro ao criar operador');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este operador?')) return;
    try {
      await api.deleteOperator(id);
      toast.success('Operador excluido com sucesso!');
      fetchOperators();
    } catch {
      toast.error('Erro ao excluir operador');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleOperator(id);
      toast.success('Status atualizado!');
      fetchOperators();
      fetchOnlineStatus();
    } catch {
      toast.error('Erro ao atualizar status');
    }
  };

  const handlePause = async (id) => {
    try {
      const res = await api.pauseOperator(id);
      toast.success(res.data.paused ? 'Operador pausado! Nao recebera novos leads.' : 'Operador reativado!');
      fetchOperators();
      fetchOnlineStatus();
    } catch {
      toast.error('Erro ao pausar operador');
    }
  };

  const handleMoveToTeam = async (operatorId, teamId) => {
    try {
      await api.addOperatorToTeam(teamId, operatorId);
      toast.success('Operador movido para a equipe!');
      fetchTeams();
    } catch {
      toast.error('Erro ao mover operador');
    }
  };

  const handleRenameTeam = async (teamId) => {
    if (!teamNameInput.trim()) return;
    try {
      await api.updateTeam(teamId, { name: teamNameInput });
      toast.success('Nome da equipe atualizado!');
      setEditingTeamName(null);
      fetchTeams();
    } catch {
      toast.error('Erro ao renomear equipe');
    }
  };

  const handleCreateTeam = async () => {
    const name = prompt('Nome da nova equipe:');
    if (!name || !name.trim()) return;
    try {
      await api.createTeam({ name: name.trim() });
      toast.success(`Equipe "${name}" criada!`);
      fetchTeams();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar equipe');
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Excluir a equipe "${teamName}"? Os operadores ficarao sem equipe.`)) return;
    try {
      await api.deleteTeam(teamId);
      toast.success('Equipe excluida!');
      fetchTeams();
    } catch {
      toast.error('Erro ao excluir equipe');
    }
  };

  const getStatusForUser = (userId) => {
    return onlineStatus.users.find(u => u.id === userId);
  };

  // Separate admins/supervisors from operators for the online status section
  // Hide Bruno's account from everyone
  const visibleUsers = onlineStatus.users.filter(u => u.email !== 'brunosorage86@gmail.com');
  const adminsAndSupervisors = visibleUsers.filter(u => u.role === 'admin' || u.role === 'supervisor');
  const operatorsStatus = visibleUsers.filter(u => u.role === 'operator');

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Operadores
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Gerencie os operadores de vendas
              </p>
            </div>

            {isPrivileged && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-operator-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Operador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Operador</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="operator-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="operator-email-input"
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="operator-phone-input"
                      />
                    </div>
                    <div>
                      <Label>Senha</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="operator-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-operator-button">
                      Criar Operador
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Online Status Section */}
          {canSeeStatus && onlineStatus.users.length > 0 && (
            <Card className="mb-6 p-6" data-testid="online-status-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">
                  Status da Equipe
                </h2>
                <Badge variant="outline" className="text-sm" data-testid="online-count-badge">
                  {visibleUsers.filter(u => u.is_online).length} online de {visibleUsers.length}
                </Badge>
              </div>

              {/* Admins & Supervisors */}
              {adminsAndSupervisors.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Gestao</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {adminsAndSupervisors.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                          u.is_online
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`user-status-${u.email}`}
                      >
                        <div className="relative shrink-0">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                            u.role === 'admin' ? 'bg-teal-600' : 'bg-blue-600'
                          }`}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <Circle
                            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${
                              u.is_online ? 'text-emerald-500 fill-emerald-500' : 'text-slate-300 fill-slate-300'
                            }`}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                          <p className="text-[10px] text-slate-400">{ROLE_LABELS[u.role] || u.role}</p>
                          {!u.is_online && u.last_seen && (
                            <p className="text-[10px] text-slate-400 flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />
                              {formatLastSeen(u.last_seen)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Operators */}
              {operatorsStatus.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Operadores</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {operatorsStatus.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                          u.paused
                            ? 'bg-amber-50 border-amber-200'
                            : u.is_online
                              ? 'bg-emerald-50 border-emerald-200'
                              : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`user-status-${u.email}`}
                      >
                        <div className="relative shrink-0">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                            u.paused ? 'bg-amber-500' : 'bg-slate-500'
                          }`}>
                            {u.paused ? <Pause className="w-4 h-4" /> : u.name.charAt(0).toUpperCase()}
                          </div>
                          {!u.paused && (
                            <Circle
                              className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${
                                u.is_online ? 'text-emerald-500 fill-emerald-500' : 'text-slate-300 fill-slate-300'
                              }`}
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                          {u.paused ? (
                            <p className="text-[10px] text-amber-600 font-semibold">PAUSADO</p>
                          ) : !u.is_online && u.last_seen ? (
                            <p className="text-[10px] text-slate-400 flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />
                              {formatLastSeen(u.last_seen)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Teams Section */}
          {isPrivileged && (
            <div className="mb-6" data-testid="teams-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-slate-900">Equipes</h2>
                {canManageTeams && (
                  <Button
                    size="sm"
                    onClick={handleCreateTeam}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="create-team-btn"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Nova Equipe
                  </Button>
                )}
              </div>
              {teams.length === 0 ? (
                <Card className="p-6 text-center">
                  <p className="text-sm text-slate-400">Nenhuma equipe criada ainda. Clique em "Nova Equipe" para comecar.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {teams.map(team => (
                    <Card key={team.id} className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        {editingTeamName === team.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={teamNameInput}
                              onChange={(e) => setTeamNameInput(e.target.value)}
                              className="h-8 w-48"
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handleRenameTeam(team.id)}
                              data-testid={`team-name-input-${team.id}`}
                            />
                            <Button variant="ghost" size="sm" onClick={() => handleRenameTeam(team.id)}>
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingTeamName(null)}>
                              <XIcon className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900">{team.name}</h3>
                            {(isSupervisor && team.supervisor_id === user?.id || canManageTeams) && (
                              <Button variant="ghost" size="sm" onClick={() => { setEditingTeamName(team.id); setTeamNameInput(team.name); }} data-testid={`rename-team-${team.id}`}>
                                <Pencil className="w-3 h-3 text-slate-400" />
                              </Button>
                            )}
                            {canManageTeams && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTeam(team.id, team.name)} data-testid={`delete-team-${team.id}`}>
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </Button>
                            )}
                          </div>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {team.supervisor_name || 'sem supervisor'} | {team.operators?.length || 0} ops
                        </Badge>
                      </div>

                      {team.operators && team.operators.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {team.operators.map(op => (
                            <div key={op.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                              <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                                {op.name.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-slate-700">{op.name}</span>
                              {op.paused && <Pause className="w-3 h-3 text-amber-500" />}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">Nenhum operador nesta equipe. Use a tabela abaixo para atribuir.</p>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All Users Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  {canSeeStatus && <TableHead className="w-16">Online</TableHead>}
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Status</TableHead>
                  {isPrivileged && <TableHead>Equipe</TableHead>}
                  {isPrivileged && <TableHead>Round-Robin</TableHead>}
                  {isPrivileged && <TableHead>Acoes</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(canSeeStatus ? onlineStatus.users.filter(u => u.email !== 'brunosorage86@gmail.com') : operators).map((userRow) => {
                  const isOperator = (userRow.role || 'operator') === 'operator';
                  const operatorData = operators.find(o => o.id === userRow.id);
                  const status = getStatusForUser(userRow.id);
                  return (
                    <TableRow key={userRow.id} data-testid={`operator-row-${userRow.id}`}>
                      {canSeeStatus && (
                        <TableCell>
                          <div className="flex items-center justify-center" data-testid={`operator-online-${userRow.id}`}>
                            <Circle
                              className={`w-3 h-3 ${
                                (status?.is_online || userRow.is_online)
                                  ? 'text-emerald-500 fill-emerald-500'
                                  : 'text-slate-300 fill-slate-300'
                              }`}
                            />
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{userRow.name}</TableCell>
                      <TableCell>{userRow.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          userRow.role === 'admin' ? 'border-teal-300 text-teal-700 bg-teal-50' :
                          userRow.role === 'supervisor' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                          'border-slate-300 text-slate-600'
                        }>
                          {ROLE_LABELS[userRow.role] || userRow.role}
                        </Badge>
                      </TableCell>
                      <TableCell>{(operatorData?.phone || userRow.phone) || '-'}</TableCell>
                      <TableCell>
                        <Badge
                          className={userRow.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                          onClick={() => isPrivileged && isOperator && handleToggle(userRow.id)}
                          style={{ cursor: isPrivileged && isOperator ? 'pointer' : 'default' }}
                        >
                          {userRow.active !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      {isPrivileged && (
                        <TableCell>
                          {isOperator && canManageTeams ? (
                            <Select
                              value={teams.find(t => t.operator_ids?.includes(userRow.id))?.id || 'none'}
                              onValueChange={(val) => val !== 'none' && handleMoveToTeam(userRow.id, val)}
                            >
                              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`team-select-${userRow.id}`}>
                                <SelectValue placeholder="Sem equipe" />
                              </SelectTrigger>
                              <SelectContent>
                                {teams.map(t => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : isOperator ? (
                            <span className="text-xs text-slate-500">
                              {teams.find(t => t.operator_ids?.includes(userRow.id))?.name || '-'}
                            </span>
                          ) : null}
                        </TableCell>
                      )}
                      {isPrivileged && (
                        <TableCell>
                          {isOperator && (
                            <Button
                              variant={userRow.paused ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePause(userRow.id)}
                              className={userRow.paused ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                              data-testid={`pause-operator-${userRow.id}`}
                            >
                              {userRow.paused ? (
                                <><Play className="w-3.5 h-3.5 mr-1" /> Reativar</>
                              ) : (
                                <><Pause className="w-3.5 h-3.5 mr-1" /> Pausar</>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      )}
                      {isPrivileged && (
                        <TableCell>
                          {isOperator && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(userRow.id)}
                              data-testid={`delete-operator-${userRow.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </div>
      </main>
    </div>
  );
}
